(() => {
  if (customElements.get('fabbricantti-cart-shipping')) return;

  const storageKey = 'fabbricantti-cart-shipping';
  const minimizedKey = `${storageKey}-minimized`;
  let minimized = false;
  let saved = { zip: '', address: '', validatedZip: '' };
  try {
    const stored = JSON.parse(sessionStorage.getItem(storageKey));
    if (stored && typeof stored.zip === 'string' && typeof stored.address === 'string' && typeof stored.validatedZip === 'string') {
      saved = stored;
    }
  } catch {}

  const persist = () => {
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(saved));
    } catch {}
  };

  const formatZip = (value) => value.replace(/\D/g, '').slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');

  class CartShipping extends HTMLElement {
    static get observedAttributes() {
      return ['data-cart-total'];
    }

    connectedCallback() {
      this.input = this.querySelector('input');
      this.button = this.querySelector('.cart-shipping__button');
      this.toggle = this.querySelector('.cart-shipping__toggle');
      this.panel = this.querySelector('.cart-shipping__body');
      this.drawer = this.closest('cart-drawer');
      this.error = this.querySelector('.cart-shipping__error');
      this.buttonLabel = this.button.textContent;
      this.results = this.querySelector('.cart-shipping__results');
      this.address = this.querySelector('.cart-shipping__address');
      this.listeners = new AbortController();
      const options = { signal: this.listeners.signal };

      this.toggle?.addEventListener('click', () => {
        this.setMinimized(this.toggle.getAttribute('aria-expanded') === 'true');
      }, options);

      this.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        this.calculate();
      }, options);
      this.input.addEventListener('input', () => {
        const previousZip = saved.zip.replace(/\D/g, '');
        this.input.value = formatZip(this.input.value);
        const zip = this.input.value.replace(/\D/g, '');
        if (zip === previousZip) return;
        this.cancelRequest();
        saved.zip = this.input.value;
        saved.address = '';
        saved.validatedZip = '';
        persist();
        this.error.hidden = true;
        this.input.removeAttribute('aria-invalid');
        this.results.hidden = true;
        this.address.hidden = true;
        if (zip.length === 8) this.calculate();
      }, options);
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.calculate();
        }
      }, options);
      document.documentElement.addEventListener('cart:updated', (event) => {
        if (Number.isFinite(event.detail?.cart?.total_price)) {
          this.setAttribute('data-cart-total', event.detail.cart.total_price);
        }
      }, options);

      this.input.value = formatZip(saved.zip);
      this.updatePrices();
      if (saved.address && saved.validatedZip === this.input.value.replace(/\D/g, '')) {
        this.showAddress(saved.address);
      }
      if (this.drawer && !this.drawer.hasAttribute('open')) minimized = false;
      this.setMinimized(minimized, false);
      if (this.drawer) {
        this.drawerObserver = new MutationObserver((records) => {
          if (this.drawer.hasAttribute('open') && records.some((record) => record.oldValue === null)) {
            this.setMinimized(false, false);
          }
        });
        this.drawerObserver.observe(this.drawer, { attributes: true, attributeFilter: ['open'], attributeOldValue: true });
        this.listeners.signal.addEventListener('abort', () => this.drawerObserver.disconnect(), { once: true });
      }
    }

    disconnectedCallback() {
      this.collapseAnimation?.cancel();
      this.listeners?.abort();
      this.cancelRequest();
    }

    attributeChangedCallback() {
      this.updatePrices();
    }

    setMinimized(value, animate = true) {
      if (!this.panel || !this.toggle) return;
      const startHeight = this.panel.getBoundingClientRect().height;
      this.collapseAnimation?.cancel();
      this.collapseAnimation = null;
      minimized = value;
      this.toggle.setAttribute('aria-expanded', String(!value));
      this.toggle.setAttribute('aria-label', value ? this.toggle.dataset.expandLabel : this.toggle.dataset.minimizeLabel);
      this.panel.toggleAttribute('inert', value);
      this.panel.setAttribute('aria-hidden', String(value));

      const finish = () => {
        this.panel.hidden = value;
        this.panel.style.overflow = '';
      };
      if (animate && this.panel.animate && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.panel.hidden = false;
        this.panel.style.overflow = 'hidden';
        // Match the theme's collapsible-content timing, starting at the current
        // height so reversing an unfinished animation stays smooth.
        const animation = this.panel.animate({
          height: [`${startHeight}px`, `${value ? 0 : this.panel.scrollHeight}px`]
        }, {
          duration: 500,
          easing: 'cubic-bezier(0.75, 0, 0.175, 1)',
          fill: 'both'
        });
        this.collapseAnimation = animation;
        animation.onfinish = () => {
          if (this.collapseAnimation !== animation) return;
          finish();
          animation.cancel();
          this.collapseAnimation = null;
        };
      } else {
        finish();
      }
      try {
        sessionStorage.setItem(minimizedKey, String(value));
      } catch {}
    }

    updatePrices() {
      const free = Number(this.dataset.cartTotal) > Number(this.dataset.freeThreshold);
      this.querySelectorAll('[data-price]').forEach((price) => price.classList.toggle('cart-shipping__original', free));
      this.querySelectorAll('[data-free]').forEach((label) => label.hidden = !free);
    }

    cancelRequest() {
      this.request?.abort();
      clearTimeout(this.timeout);
      this.setLoading(false);
      this.removeAttribute('aria-busy');
    }

    setLoading(value) {
      if (!this.button) return;
      this.button.disabled = value;
      if (value) {
        const label = document.createElement('span');
        label.textContent = this.button.dataset.loadingLabel;
        const dots = document.createElement('span');
        dots.className = 'cart-shipping__dots';
        dots.setAttribute('aria-hidden', 'true');
        this.button.replaceChildren(label, dots);
        this.button.setAttribute('aria-label', `${this.button.dataset.loadingLabel}...`);
      } else {
        this.button.textContent = this.buttonLabel;
        this.button.removeAttribute('aria-label');
      }
    }

    showError(message) {
      this.error.textContent = message;
      this.error.hidden = false;
      this.input.setAttribute('aria-invalid', 'true');
    }

    showAddress(value) {
      this.querySelector('[data-address]').textContent = value;
      this.address.hidden = false;
      this.updatePrices();
      this.results.hidden = false;
    }

    async calculate() {
      if (this.button.disabled) return;
      this.cancelRequest();
      const zip = this.input.value.replace(/\D/g, '');
      this.error.hidden = true;
      this.input.removeAttribute('aria-invalid');
      this.results.hidden = true;
      saved = { zip: this.input.value, address: '', validatedZip: '' };
      persist();

      if (zip.length !== 8) {
        this.showError(this.error.dataset.invalid);
        this.input.focus();
        return;
      }

      this.request = new AbortController();
      const request = this.request;
      let timedOut = false;
      this.timeout = setTimeout(() => {
        timedOut = true;
        request.abort();
      }, 10000);
      const loadingStarted = performance.now();
      this.setLoading(true);
      this.setAttribute('aria-busy', 'true');

      try {
        const response = await fetch(`https://viacep.com.br/ws/${zip}/json/`, { signal: request.signal });
        if (!response.ok) throw new Error('CEP lookup failed');
        const data = await response.json();
        if (request.signal.aborted || !this.isConnected) return;
        if (data.erro || !data.localidade || !data.uf) {
          this.showError(this.error.dataset.notFound);
          return;
        }
        const remaining = 900 - (performance.now() - loadingStarted);
        if (remaining > 0) {
          await new Promise((resolve) => {
            const finish = () => {
              clearTimeout(timer);
              request.signal.removeEventListener('abort', finish);
              resolve();
            };
            const timer = setTimeout(finish, remaining);
            request.signal.addEventListener('abort', finish, { once: true });
          });
        }
        if (request.signal.aborted || !this.isConnected) return;
        const address = `${data.logradouro ? data.logradouro + ', ' : ''}${data.localidade} - ${data.uf}`;
        saved = { zip: this.input.value, address, validatedZip: zip };
        persist();
        this.showAddress(address);
      } catch {
        if (this.isConnected && (!request.signal.aborted || timedOut)) {
          this.showError(this.error.dataset.unavailable);
        }
      } finally {
        if (this.request === request) {
          clearTimeout(this.timeout);
          this.setLoading(false);
          this.removeAttribute('aria-busy');
        }
      }
    }
  }

  customElements.define('fabbricantti-cart-shipping', CartShipping);
})();
